/** Greece (Athens) timezone for member-facing dates and greetings. */
export const ATHENS_TIME_ZONE = 'Europe/Athens';

function getLocaleTag(language: string): string {
  return language === 'gr' || language.startsWith('gr') ? 'el-GR' : 'en-GB';
}

/** Current hour of day (0–23) in Europe/Athens. */
export function getAthensHour(now: Date = new Date()): number {
  const hourPart = new Intl.DateTimeFormat('en-GB', {
    timeZone: ATHENS_TIME_ZONE,
    hour: 'numeric',
    hour12: false,
  })
    .formatToParts(now)
    .find(p => p.type === 'hour')?.value;
  const hour = hourPart != null ? parseInt(hourPart, 10) : now.getHours();
  // Some environments emit "24" for midnight.
  return hour === 24 ? 0 : hour;
}

export type AthensGreetingPeriod = 'morning' | 'afternoon' | 'evening';

export function getAthensGreetingPeriod(now: Date = new Date()): AthensGreetingPeriod {
  const hour = getAthensHour(now);
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

/**
 * Parse API timestamps as UTC instants.
 * DateTime.UtcNow is stored as datetime2 (Unspecified). Without a Z/offset, ES treats
 * ISO strings as local time, which then shifts again when formatted in Europe/Athens.
 */
export function parseUtcInstant(dateStr: string): Date | null {
  const trimmed = dateStr.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const dateOnly = new Date(`${trimmed}T12:00:00Z`);
    return Number.isNaN(dateOnly.getTime()) ? null : dateOnly;
  }
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const iso = hasZone ? trimmed : `${trimmed}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Calendar date in Athens (day / month / year). */
export function formatAthensDate(dateStr: string | undefined, language: string): string {
  if (!dateStr) return '';
  const date = parseUtcInstant(dateStr) ?? new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(getLocaleTag(language), {
    timeZone: ATHENS_TIME_ZONE,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

/** Date and time in Athens, with an explicit timezone label. */
export function formatAthensDateTime(dateStr: string | undefined, language: string): string {
  if (!dateStr) return '';
  const date = parseUtcInstant(dateStr) ?? new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  const datePart = formatAthensDate(dateStr, language);
  const timePart = new Intl.DateTimeFormat(getLocaleTag(language), {
    timeZone: ATHENS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
  const tzLabel =
    language === 'gr' || language.startsWith('gr') ? 'Ώρα Ελλάδας' : 'Europe/Athens';
  return `${datePart}, ${timePart} (${tzLabel})`;
}

/** Relative/activity timestamp using Athens calendar day boundaries. */
export function formatAthensActivityTimestamp(timestamp: string, language: string): string {
  const date = parseUtcInstant(timestamp) ?? new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const intlLocale = getLocaleTag(language);
  const isGreek = language === 'gr' || language.startsWith('gr');

  const athensDayKey = (d: Date) =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: ATHENS_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);

  const todayKey = athensDayKey(new Date());
  const thenKey = athensDayKey(date);
  const todayUtc = Date.parse(`${todayKey}T00:00:00Z`);
  const thenUtc = Date.parse(`${thenKey}T00:00:00Z`);
  const dayDiff = Math.round((todayUtc - thenUtc) / 86400000);

  const time = new Intl.DateTimeFormat(intlLocale, {
    timeZone: ATHENS_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

  if (dayDiff === 0) {
    return isGreek ? `Σήμερα, ${time}` : `Today, ${time}`;
  }
  if (dayDiff === 1) {
    return isGreek ? `Χθες, ${time}` : `Yesterday, ${time}`;
  }
  if (dayDiff > 1 && dayDiff < 7) {
    return isGreek ? `Πριν ${dayDiff} ημέρες` : `${dayDiff} days ago`;
  }

  return formatAthensDate(timestamp, language);
}
